import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
  type DependencyList,
} from "react";
import { useAuth } from "../../../lib/hooks/use-auth";

/**
 * Sprint 9a — the right rail is a per-route slot owned by AppShell but filled by
 * whichever route is mounted. Routes register their right-rail JSX via
 * {@link useRightRail}. When no content is registered the rail auto-collapses to zero
 * width, so non-cockpit routes get the full main column for free (opt-in, no prop
 * drilling).
 *
 * The user's expand/collapse choice persists per workspace in localStorage
 * (`kuvio_right_rail_expanded_${workspaceId}`), mirroring the left-rail
 * expansion pattern in ScopeRail.
 *
 * №22.5 (ride-along, №29 item 7) — the `RightRail` COMPONENT this used to name is
 * gone. №29's shell put the work rail on the shell itself, which left the Sprint-9a
 * renderer imported by nothing while its state hook stayed live (`RowDetailSidePanel`
 * still uses `useRightRailState`). Deleted rather than left as a second rail
 * implementation waiting to be found and re-wired — the state layer here is the part
 * that is still real.
 */

interface RightRailContextValue {
  /** Currently registered right-rail content (null = nothing, rail collapses). */
  content: ReactNode;
  setContent: (content: ReactNode) => void;
  /** User preference: is the rail expanded? Default true, persisted per workspace. */
  expanded: boolean;
  setExpanded: (value: boolean) => void;
  toggleExpanded: () => void;
}

const RightRailContext = createContext<RightRailContextValue | null>(null);

function storageKeyFor(workspaceId: string | null | undefined): string | null {
  return workspaceId ? `kuvio_right_rail_expanded_${workspaceId}` : null;
}

function loadExpanded(key: string | null): boolean {
  if (!key) return true; // default: expanded
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return true;
    return raw === "1";
  } catch {
    return true;
  }
}

function persistExpanded(key: string | null, value: boolean): void {
  if (!key) return;
  try {
    localStorage.setItem(key, value ? "1" : "0");
  } catch {
    /* ignore */
  }
}

export function RightRailProvider({ children }: { children: ReactNode }) {
  const { currentWorkspaceId } = useAuth();
  const key = storageKeyFor(currentWorkspaceId);

  const [content, setContent] = useState<ReactNode>(null);
  const [expanded, setExpandedState] = useState<boolean>(() => loadExpanded(key));

  // Re-hydrate the preference when the workspace changes.
  useEffect(() => {
    setExpandedState(loadExpanded(key));
  }, [key]);

  const setExpanded = useCallback(
    (value: boolean) => {
      setExpandedState(value);
      persistExpanded(key, value);
    },
    [key],
  );

  const toggleExpanded = useCallback(() => {
    setExpandedState((cur) => {
      const next = !cur;
      persistExpanded(key, next);
      return next;
    });
  }, [key]);

  const value: RightRailContextValue = {
    content,
    setContent,
    expanded,
    setExpanded,
    toggleExpanded,
  };

  return <RightRailContext.Provider value={value}>{children}</RightRailContext.Provider>;
}

export function useRightRailState(): RightRailContextValue {
  const ctx = useContext(RightRailContext);
  if (!ctx) {
    throw new Error("useRightRailState must be used within a RightRailProvider");
  }
  return ctx;
}

/**
 * Register right-rail content for the current route. Sets the content on mount
 * and whenever `deps` change, and clears it on unmount so the rail collapses
 * when you navigate away. `deps` follows useEffect semantics — pass the values
 * the content depends on (e.g. `[nodeId]`); the element itself is read from a
 * ref so a fresh element on every render does not retrigger the effect.
 *
 * Pass `null` to explicitly keep the rail collapsed on a route (e.g. TablePage,
 * which wants the full main width).
 */
export function useRightRail(content: ReactNode, deps: DependencyList = []): void {
  const { setContent } = useRightRailState();
  const contentRef = useRef<ReactNode>(content);
  contentRef.current = content;

  useEffect(() => {
    setContent(contentRef.current);
    return () => setContent(null);
    // setContent is a stable useState dispatcher; deps drive content refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setContent, ...deps]);
}
